import React from "react";
import { ArrowRight, ListChecks, Lock, Sparkles, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button.tsx";
import { providerMeta } from "@/pages/vb/run/providerMeta";
import { useWorkbenchQueue } from "@/hooks/workbench/useWorkbench";
import { isOutstanding } from "@/pages/workbench/workbenchUtil";
import type { WorkbenchFix } from "@/types/workbench";

/** "The next thing to do" — the report-dashboard hero that replaces the old
 *  "AI Trust Signals to Improve" list. It surfaces the single highest-priority
 *  actionable fix (from the MW-1 workbench queue, so HTTPS/composites are already
 *  excluded and the friendly title + AI evidence come for free), a one-click way
 *  into the Workbench, and a health rollup of every AI Trust Signal in the report.
 *
 *  Effort is shown as the fix's step count (there is no effort field on the
 *  model) rather than an invented "~30 minutes", so the numbers stay real. */
interface Props {
  report: any;
  companyId?: number;
  onStart?: () => void;
  isFree?: boolean;
  onUnlock?: () => void;
}

/** Brand fill behind each model's mark — the shared --vb-model-* tokens (AITS-490). */
const MODEL_BG: Record<string, string> = {
  openai: "var(--vb-model-openai)",
  anthropic: "var(--vb-model-anthropic)",
  claude: "var(--vb-model-anthropic)",
  gemini: "var(--vb-model-gemini)",
  perplexity: "var(--vb-model-perplexity)",
  unknown: "var(--vb-text-3)",
  generic: "var(--vb-text-3)",
};

/** Strip the markdown markers + links models pepper into answers and clamp to a
 *  teaser, so the quote reads as clean prose. */
function plainExcerpt(text: string, max = 200): string {
  const t = (text || "")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1") // [label](url) -> label
    .replace(/\(https?:\/\/[^)]*\)/g, "") // bare (https://…) parentheticals
    .replace(/https?:\/\/\S+/g, "") // stray URLs
    .replace(/[*_`#>]+/g, "")
    .replace(/^\s*[-•]\s+/gm, "")
    .replace(/\(\s*\)/g, "") // empty () left behind
    .replace(/\s+([.,;:!?])/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
  if (!t) return "";
  return t.length > max ? t.slice(0, max).trimEnd() + "…" : t;
}

/** "checked today" / "checked 2 days ago" from the report's scan date. */
function checkedAgo(iso?: string | null): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return "checked today";
  if (days === 1) return "checked yesterday";
  if (days < 7) return `checked ${days} days ago`;
  const weeks = Math.floor(days / 7);
  if (weeks === 1) return "checked last week";
  if (days < 60) return `checked ${weeks} weeks ago`;
  const months = Math.floor(days / 30);
  return `checked ${months} months ago`;
}

/** Passing signals across the whole report — a positive aside with its own,
 *  explicit denominator so it never reads as part of the fix math. */
function reportHealth(report: any): { healthy: number; total: number } {
  const cats = ["technical", "authority", "brand"] as const;
  const all = cats.flatMap((c) => report?.signals?.[c] ?? []);
  let healthy = 0;
  for (const s of all) {
    const score = String(s?.score ?? "").toLowerCase();
    if (score === "pass" || score === "high") healthy++;
  }
  return { healthy, total: all.length };
}

function CardShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-2xl border-2 border-vb-brand bg-vb-surface shadow-vb-sm">
      {children}
    </div>
  );
}

function Ribbon({ label, step }: { label: string; step?: string }) {
  return (
    <div className="inline-flex items-center gap-2 self-start rounded-md bg-vb-brand px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.14em] text-white">
      {label}
      {step && (
        <span className="rounded bg-white/20 px-1.5 py-0.5 text-[10px] font-bold tracking-normal tabular-nums">
          {step}
        </span>
      )}
    </div>
  );
}

/** Footer: the fix-queue breakdown (urgent + medium sum to the queue size shown
 *  in the ribbon) plus a separate, explicitly-denominated "healthy" aside. The
 *  two lines use different denominators on purpose — the queue is the actionable
 *  workbench fixes; healthy is every passing signal in the report — so they're
 *  split rather than sat in one row that invites a wrong sum. */
function HealthFooter({
  report,
  urgent,
  medium,
  waitingLine,
}: {
  report: any;
  urgent: number;
  medium: number;
  waitingLine: string;
}) {
  const { healthy, total } = reportHealth(report);
  return (
    <div className="mt-auto border-t border-vb-line bg-vb-bg-subtle px-7 py-4">
      <div className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-vb-text-3">Workbench</div>
      <div className="mt-0.5 text-sm font-bold leading-tight text-vb-text">{waitingLine}</div>
      {urgent + medium > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs font-semibold tabular-nums text-vb-text-2">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-[7px] w-[7px] rounded-full bg-vb-fail" />
            {urgent} urgent (Low / Fail)
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-[7px] w-[7px] rounded-full bg-vb-warn" />
            {medium} medium
          </span>
        </div>
      )}
      <div className="mt-1 flex items-center gap-1.5 text-xs font-medium tabular-nums text-vb-text-3">
        <span className="h-[7px] w-[7px] rounded-full bg-vb-pass" />
        {healthy} of {total} AI Trust Signals already healthy
      </div>
    </div>
  );
}

const NextThingCard = ({ report, companyId, onStart, isFree, onUnlock }: Props) => {
  const enabled = !isFree && companyId != null;
  const { data, isLoading } = useWorkbenchQueue(companyId, enabled);

  const reportDate = data?.report?.created_at ?? null;
  const fixes: WorkbenchFix[] = data?.fixes ?? [];
  const outstanding = fixes.filter((f) => isOutstanding(f, reportDate));
  const top = outstanding[0] ?? null;

  // Severity split of the *actionable queue* — sums to outstanding.length, so the
  // footer reconciles with the ribbon's "1 of N" (unlike a report-wide band count).
  const fixUrgent = outstanding.filter((f) => f.severity === "critical").length;
  const fixMedium = outstanding.filter((f) => f.severity === "issue").length;

  // ── Free: locked, subscribe to unlock the fix plan ──
  if (isFree) {
    return (
      <CardShell>
        <div className="flex flex-1 flex-col gap-3 p-7 pb-6">
          <Ribbon label="Your fix plan" />
          <h2 className="text-[22px] font-bold leading-tight tracking-tight text-vb-text">
            Unlock your AI fix plan.
          </h2>
          <p className="text-sm leading-relaxed text-vb-text-3">
            See exactly what AI gets wrong about your business — and fix it, one prioritised step at a
            time, in the Workbench.
          </p>
          <div className="mt-1">
            <Button variant="gold" onClick={onUnlock} className="flex items-center">
              <Lock className="mr-2 h-4 w-4" />
              Subscribe to open the workbench
            </Button>
          </div>
        </div>
      </CardShell>
    );
  }

  // ── Loading the queue ──
  if (enabled && isLoading) {
    return (
      <CardShell>
        <div className="flex flex-1 flex-col gap-3 p-7 pb-6">
          <div className="h-6 w-40 animate-pulse rounded-md bg-vb-bg-subtle" />
          <div className="h-8 w-3/4 animate-pulse rounded-md bg-vb-bg-subtle" />
          <div className="h-20 w-full animate-pulse rounded-[10px] bg-vb-bg-subtle" />
          <div className="h-10 w-32 animate-pulse rounded-lg bg-vb-bg-subtle" />
        </div>
        <HealthFooter report={report} urgent={0} medium={0} waitingLine="Loading your fixes…" />
      </CardShell>
    );
  }

  // ── All clear: nothing outstanding ──
  if (!top) {
    return (
      <CardShell>
        <div className="flex flex-1 flex-col gap-3 p-7 pb-6">
          <Ribbon label="You're all clear" />
          <h2 className="flex items-center gap-2 text-[22px] font-bold leading-tight tracking-tight text-vb-text">
            <CheckCircle2 className="h-6 w-6 shrink-0 text-vb-pass" />
            No fixes needed right now.
          </h2>
          <p className="text-sm leading-relaxed text-vb-text-3">
            Every actionable AI Trust Signal on this report is in good shape. We'll re-check on your next
            monthly report.
          </p>
        </div>
        <HealthFooter report={report} urgent={0} medium={0} waitingLine="Nothing waiting — your workbench is clear." />
      </CardShell>
    );
  }

  // ── The next thing to do ──
  const meta = providerMeta(top.evidence[0]?.provider ?? "");
  const quote = plainExcerpt(top.evidence[0]?.response ?? "");
  const stepCount = top.steps.length;
  const quickWin = stepCount > 0 && stepCount <= 2;
  const moreWaiting = outstanding.length - 1;
  const waitingLine =
    moreWaiting > 0
      ? `${moreWaiting} more fix${moreWaiting === 1 ? "" : "es"} waiting in your workbench.`
      : "This is your last open fix — nice.";

  return (
    <CardShell>
      <div className="flex flex-1 flex-col gap-4 p-7 pb-6">
        <Ribbon label="The next thing to do" step={`1 of ${outstanding.length}`} />

        <h2 className="max-w-[640px] text-[22px] font-bold leading-tight tracking-tight text-vb-text sm:text-[26px]">
          {top.title}
        </h2>

        {quote && (
          <div className="flex flex-col gap-1.5 rounded-[10px] border border-vb-line bg-vb-bg-subtle p-3.5">
            <div className="flex items-center gap-2 text-[11.5px] font-bold text-vb-text">
              <span
                className="grid h-[22px] w-[22px] place-items-center rounded-[5px] text-white [&>svg]:h-[13px] [&>svg]:w-[13px]"
                style={{ background: MODEL_BG[meta.className] ?? MODEL_BG.unknown }}
                aria-hidden="true"
              >
                {meta.logo}
              </span>
              {meta.label}
              <span className="ml-auto text-[11px] font-medium text-vb-text-3">
                {checkedAgo(reportDate)}
              </span>
            </div>
            <p className="text-sm italic leading-relaxed text-vb-text-2">“{quote}”</p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          {/* Hover darkens a constant brand fill via filter, NOT a background-color
              swap. The old `transition-colors hover:bg-vb-brand-hover` transitioned a
              var()-backed background; on some browsers/GPUs that hover paint failed and
              left the white label on a blank card, so the button vanished (AITS-599).
              A constant `bg-vb-brand` + brightness filter can only darken, never blank. */}
          <button
            type="button"
            onClick={onStart}
            className="inline-flex items-center gap-2 rounded-lg bg-vb-brand px-4 py-2.5 text-sm font-semibold text-white transition hover:brightness-90"
          >
            Start now
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
          <div className="ml-auto flex items-center gap-3.5 text-xs font-semibold text-vb-text-3">
            {stepCount > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <ListChecks className="h-3.5 w-3.5" />
                {stepCount} step{stepCount === 1 ? "" : "s"}
              </span>
            )}
            {quickWin && (
              <span className="inline-flex items-center gap-1 text-vb-pass-ink">
                <Sparkles className="h-3.5 w-3.5" />
                Quick win
              </span>
            )}
          </div>
        </div>
      </div>

      <HealthFooter report={report} urgent={fixUrgent} medium={fixMedium} waitingLine={waitingLine} />
    </CardShell>
  );
};

export default NextThingCard;
